import { ButtonLoading } from '@tovala/component-library'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useResetPassword } from '@tovala/browser-apis-combinedapi'

import {
  ErrorCodeMessageMapCombinedAPI,
  GoodErrorMessaging,
  LoginLocationState,
} from 'types/internal'
import { events } from 'analytics/events'
import { getUserIdFromJWT } from 'utils/auth'
import { track } from 'utils/analytics'

import { useToast } from 'contexts/toast'
import APIErrorDisplay from 'components/common/APIErrorDisplay'
import AuthPage from './AuthPage'
import ErrorDisplay from 'components/common/ErrorDisplay'
import FormInputRHF from 'components/common/FormInputRHF'
import { useForm } from 'react-hook-form'

interface FormData {
  newPassword: ''
  newPasswordConfirm: ''
}

const INVALID_TOKEN_HELP: GoodErrorMessaging = {
  helpToFix: (
    <span>
      Please{' '}
      <Link className="underline" to="/forgot-password">
        request a new reset link
      </Link>
      .
    </span>
  ),
  why: "We couldn't reset your password because your link has expired.",
}

const RESET_PASSWORD_ERRORS: ErrorCodeMessageMapCombinedAPI = {
  Fallback: {
    helpToFix: 'Please try reloading the page.',
    why: "We couldn't reset your password due to a technical issue on our end.",
  },
  InvalidToken: INVALID_TOKEN_HELP,
}

const ResetPasswordPage = () => {
  const { resetToken } = useParams<{ resetToken: string }>()

  let userIDFromJWT: number | undefined
  if (resetToken) {
    userIDFromJWT = getUserIdFromJWT(resetToken)
  }

  return (
    <AuthPage>
      <div className="flex justify-center">
        <div className="flex flex-col items-center md:w-full">
          <h1 className="mb-12 text-center text-k/44_110 font-medium md:mb-10 md:text-k/32_105">
            Reset Password
          </h1>

          <div className="w-[320px] md:w-full">
            {resetToken && userIDFromJWT ? (
              <ResetPasswordForm
                resetToken={resetToken}
                userID={userIDFromJWT}
              />
            ) : (
              <ErrorDisplay {...INVALID_TOKEN_HELP} />
            )}
          </div>
        </div>
      </div>
    </AuthPage>
  )
}

export default ResetPasswordPage

const ResetPasswordForm = ({
  resetToken,
  userID,
}: {
  resetToken: string
  userID: number
}) => {
  const navigate = useNavigate()
  const { openToast } = useToast()

  const {
    formState: { errors },
    handleSubmit,
    register,
    watch,
  } = useForm<FormData>({
    defaultValues: {
      newPassword: '',
      newPasswordConfirm: '',
    },
  })

  const {
    error: resetPasswordError,
    isError: hasResetPasswordError,
    isPending: isResettingPassword,
    mutate: resetPassword,
  } = useResetPassword({
    onError: () => {
      track(events.RESET_PASSWORD_FAILED)
    },
    onSuccess: () => {
      track(events.RESET_PASSWORD_SUCCESSFUL)

      openToast({
        heading: 'Success!',
        message: 'Your password was reset.',
        type: 'success',
      })

      const state: LoginLocationState = { passwordReset: true }

      navigate('/login', { state })
    },
  })

  return (
    <form
      onSubmit={handleSubmit((data) => {
        track(events.RESET_PASSWORD_ATTEMPT)

        return resetPassword({
          data: { password: data.newPassword, token: resetToken },
          userID,
        })
      })}
    >
      <div className="space-y-6 sm:space-y-4">
        <FormInputRHF
          error={errors?.newPassword?.message}
          id="newPassword"
          label="New Password"
          labelFor="newPassword"
          type="password"
          {...register('newPassword', {
            required: 'Please provide a new password',
          })}
        />
        <FormInputRHF
          error={errors?.newPasswordConfirm?.message}
          id="newPasswordConfirm"
          label="Confirm New Password"
          labelFor="newPasswordConfirm"
          type="password"
          {...register('newPasswordConfirm', {
            required: 'Please confirm your new password',
            validate: (value) =>
              value === watch('newPassword') || 'Passwords do not match',
          })}
        />

        {hasResetPasswordError && (
          <APIErrorDisplay
            error={resetPasswordError}
            errorCodeMessageMap={RESET_PASSWORD_ERRORS}
          />
        )}
      </div>

      <div className="mt-8 flex justify-center sm:mt-6">
        <ButtonLoading
          isLoading={isResettingPassword}
          size="large"
          type="submit"
        >
          Change Password
        </ButtonLoading>
      </div>
    </form>
  )
}
